// src/features/inventory/pages/ItemDetailPage/tabs/BasicInfo/index.jsx
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Box, Chip, Link, Typography } from '@mui/material';
import {
  Category as CategoryIcon,
  Description as DescriptionIcon,
  InfoOutlined as InfoIcon,
  Inventory as InventoryIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import LocalizedDateDistance from '../../../../../../i18n/LocalizedDateDistance';
import SectionHeader from '../../../../../../components/common/SectionHeader';
import { useStockTransactions } from '../../../../hooks/useStockTransactions';
import { useInventoryStockPermissions } from '../../../../../auth/hooks/usePermissions';

export default function BasicInfo({ item, itemId }) {
  const { t } = useTranslation(['inventory', 'common']);
  const [, setSearchParams] = useSearchParams();
  const { transactions, fetchTransactions } = useStockTransactions();

  const { canRead: canReadStock } = useInventoryStockPermissions();
  // Stock history is shown when inventory is tracked and the user may see stock
  const inventoryTracked = (item.product_group?.inventory_tracked ?? false) && canReadStock;

  useEffect(() => {
    // TODO: Include limit to the fetch API call
    if (inventoryTracked) fetchTransactions(itemId);
  }, [fetchTransactions, itemId, inventoryTracked]);

  const recentTransactions = transactions.slice(0, 5);

  const handleSeeFullLog = (e) => {
    e.preventDefault();
    setSearchParams(params => {
      const next = new URLSearchParams(params);
      next.set('tab', 'stock_log');
      return next;
    });
  };

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: inventoryTracked ? '3fr 2fr' : '1fr' },
      gap: 4,
      p: 4,
    }}>

      {/* Left: Details + Description */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

        {/* Details grid */}
        <Box>
          <SectionHeader icon={InfoIcon} title={t('inventory:items.tabs.basic_info.details')} />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t('inventory:items.create_form.uom')}
              </Typography>
              <Typography variant="body1">{item.uom}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t('inventory:items.detail.added')}
              </Typography>
              <Typography variant="body1">
                <LocalizedDateDistance date={item.created_at} />
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Description */}
        <Box>
          <SectionHeader icon={DescriptionIcon} title={t('inventory:items.create_form.description')} />
          <Box sx={{
            bgcolor: 'background.default',
            p: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            minHeight: 80,
          }}>
            {item.description ? (
              <Typography variant="body1" sx={{ lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                {item.description}
              </Typography>
            ) : (
              <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                {t('inventory:items.detail.no_description')}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Right: Quick stock history — only when inventory is tracked */}
      {inventoryTracked && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Quick stock history */}
        <Box>
          <SectionHeader icon={InventoryIcon} title={t('inventory:items.tabs.basic_info.quick_stock')} />
          {recentTransactions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t('inventory:items.detail.no_stock_data')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {recentTransactions.map((tx) => (
                <Box
                  key={tx.stock_transaction_id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.25,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {tx.quantity_change >= 0
                      ? <TrendingUpIcon fontSize="small" color="success" />
                      : <TrendingDownIcon fontSize="small" color="error" />
                    }
                    <Typography
                      variant="body2"
                      color={tx.quantity_change >= 0 ? 'success.main' : 'error.main'}
                      fontWeight="medium"
                    >
                      {tx.quantity_change >= 0 ? '+' : ''}{tx.quantity_change}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{item.uom}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    <LocalizedDateDistance date={tx.created_at} />
                  </Typography>
                </Box>
              ))}
              <Link
                component="button"
                variant="body2"
                onClick={handleSeeFullLog}
                sx={{ alignSelf: 'flex-start', mt: 1.5 }}
              >
                {t('inventory:items.tabs.basic_info.see_full_log')} →
              </Link>
            </Box>
          )}
        </Box>
      </Box>}
    </Box>
  );
}
